/* -------------------------------------------------------------------------------------------------
 * Common Compound Indexes
 * -----------------------------------------------------------------------------------------------*/

import {
  CreateIndexedMapParams,
  IndexedMapCompoundIndex,
} from '@basehub/utils/indexed-map/index'
import { BlockRelationship } from '.'
import { RootBlock } from '../block'
import { isBRMoreImportant } from '../type-casts'

export type CommonBRsCompoundIndexes = {
  byParentId: IndexedMapCompoundIndex<
    BlockRelationship,
    Map<string, Set<string>>
  >
  byBlockOrSourceBlockId: IndexedMapCompoundIndex<
    BlockRelationship,
    Map<string, Set<string>>
  >
}

export const brsByParentId = {
  updateIndex: ({ item, currentValue, oldItem, operation }) => {
    const parentMap =
      currentValue ?? (new Map() as NonNullable<typeof currentValue>)

    if (operation === 'upsert') {
      // Remove from old parent if it exists
      if (oldItem && oldItem.parentId !== item.parentId) {
        const oldChildren = parentMap.get(oldItem.parentId)
        if (oldChildren) {
          oldChildren.delete(item.id)
          if (oldChildren.size === 0) {
            parentMap.delete(oldItem.parentId)
          }
        }
      }

      // Add to new parent
      let children = parentMap.get(item.parentId)
      if (!children) {
        children = new Set<string>()
        parentMap.set(item.parentId, children)
      }
      children.add(item.id)
    } else {
      const children = parentMap.get(item.parentId)
      if (children) {
        children.delete(item.id)
        if (children.size === 0) {
          parentMap.delete(item.parentId)
        }
      }
    }

    return parentMap
  },
  cloneIndex: (currentValue) => {
    const cloned = new Map<string, Set<string>>()
    for (const [key, set] of currentValue.entries()) {
      cloned.set(key, new Set(set))
    }
    return cloned
  },
} satisfies CommonBRsCompoundIndexes['byParentId']

export const brsByBlockOrSourceBlockId = {
  updateIndex: ({ item, key, operation, currentValue }) => {
    const map = currentValue ?? (new Map() as NonNullable<typeof currentValue>)

    // Update blockId index
    if (operation === 'upsert') {
      let set = map.get(item.blockId)
      if (!set) {
        set = new Set<string>()
        map.set(item.blockId, set)
      }
      set.add(key)
    } else {
      const set = map.get(item.blockId)
      if (set) {
        set.delete(key)
        if (set.size === 0) {
          map.delete(item.blockId)
        }
      }
    }

    // Only update sourceBlockId index if it's different from blockId
    if (item.sourceBlockId !== item.blockId) {
      if (operation === 'upsert') {
        let set = map.get(item.sourceBlockId)
        if (!set) {
          set = new Set<string>()
          map.set(item.sourceBlockId, set)
        }
        set.add(key)
      } else {
        const set = map.get(item.sourceBlockId)
        if (set) {
          set.delete(key)
          if (set.size === 0) {
            map.delete(item.sourceBlockId)
          }
        }
      }
    }

    return map
  },
  cloneIndex: (currentValue) => {
    const cloned = new Map<string, Set<string>>()
    for (const [key, set] of currentValue.entries()) {
      cloned.set(key, new Set(set))
    }
    return cloned
  },
} satisfies CommonBRsCompoundIndexes['byBlockOrSourceBlockId']

export const commonBRsCompoundIndexes = {
  byParentId: brsByParentId,
  byBlockOrSourceBlockId: brsByBlockOrSourceBlockId,
} satisfies CommonBRsCompoundIndexes

/* -------------------------------------------------------------------------------------------------
 * Indexed Map Helpers
 * -----------------------------------------------------------------------------------------------*/

export const getOnIndexConflictHandler = (
  getRootBlock: () => RootBlock
): NonNullable<
  CreateIndexedMapParams<
    BlockRelationship,
    CommonBRsCompoundIndexes
  >['onIndexConflict']
> => {
  return ({ indexKey, existingItem, item }) => {
    if (indexKey !== 'sourceBlockId' && indexKey !== 'blockId') {
      throw new Error(
        `you probably shouldn't index keys other than sourceBlockId or blockId: ${indexKey}`
      )
    }

    const debug = false
    if (debug) {
      // eslint-disable-next-line no-console
      console.log('getOnIndexConflictHandler', {
        rb: getRootBlock(),
        indexKey,
        existingItem,
        item,
      })
    }

    /**
     * This might be due to copy-on-write. We'll keep the one that earlier in the root lineage.
     */
    if (isBRMoreImportant(item, existingItem, getRootBlock())) {
      if (debug) {
        // eslint-disable-next-line no-console
        console.log('keeping last')
      }
      return { keep: 'last', deleteUnused: true }
    } else {
      if (debug) {
        // eslint-disable-next-line no-console
        console.log('keeping first')
      }
      return { keep: 'first', deleteUnused: true }
    }
  }
}
